import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tab,
    Tabs,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import type {AuditPeriodSlice} from '@/utils/auditPeriodSplit';
import {downloadFsPreviewZip} from '@/services/auditService/auditFsPreviewDownloadService';
import {BsPlPreviewPanel} from '@/pages/components/audit/adjustment/preview/tabs/BsPlPreviewPanel';
import {TaxComPreviewPanel} from '@/pages/components/audit/adjustment/preview/tabs/TaxComPreviewPanel';
import {TaxReturnPreviewPanel} from '@/pages/components/audit/adjustment/preview/tabs/TaxReturnPreviewPanel';

type PreviewTabKey = 'BS_PL' | 'TAX_COM' | 'TAX_RETURN';

interface FsPreviewDownloadDialogProps {
    open: boolean;
    onClose: () => void;
    companyId: number;
    annualId: number;
    periods?: AuditPeriodSlice[];
    initialYearOfAssessment?: string | null;
    onSnack?: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const TAB_DEFS: Array<{key: PreviewTabKey; label: string}> = [
    {key: 'BS_PL', label: 'BS & PL'},
    {key: 'TAX_COM', label: 'Tax Com'},
    {key: 'TAX_RETURN', label: 'Tax Return'},
];

/** Unified Preview Dialog: three tabs + zip download (zip still includes AFS Excel). */
export function FsPreviewDownloadDialog({
    open,
    onClose,
    companyId,
    annualId,
    periods,
    initialYearOfAssessment,
    onSnack,
}: FsPreviewDownloadDialogProps) {
    const [tab, setTab] = useState<PreviewTabKey>('BS_PL');
    const [visited, setVisited] = useState<Set<PreviewTabKey>>(() => new Set(['BS_PL']));
    const [exportingZip, setExportingZip] = useState(false);

    const yoa = useMemo(
        () => (initialYearOfAssessment || '').trim() || undefined,
        [initialYearOfAssessment],
    );

    useEffect(() => {
        if (!open) return;
        setTab('BS_PL');
        setVisited(new Set(['BS_PL']));
    }, [open, companyId, annualId]);

    const handleTabChange = (_: React.SyntheticEvent, next: PreviewTabKey) => {
        setTab(next);
        setVisited((prev) => {
            if (prev.has(next)) return prev;
            const copy = new Set(prev);
            copy.add(next);
            return copy;
        });
    };

    const handleDownloadZip = useCallback(async () => {
        if (exportingZip || !companyId || !annualId) return;
        try {
            setExportingZip(true);
            const {blob, filename} = await downloadFsPreviewZip(companyId, annualId, yoa);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'download.zip';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            onSnack?.('Download FS Preview zip successful!', 'success');
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Download zip failed.';
            onSnack?.(msg, 'error');
        } finally {
            setExportingZip(false);
        }
    }, [exportingZip, companyId, annualId, yoa, onSnack]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {maxWidth: 1320, height: '90vh', display: 'flex', flexDirection: 'column'},
            }}
        >
            <DialogTitle sx={{m: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Box sx={{fontWeight: 700}}>Preview</Box>
                    <Box sx={{ml: 'auto', display: 'flex', alignItems: 'center', gap: 1}}>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={
                                exportingZip
                                    ? <CircularProgress size={14} color="inherit"/>
                                    : <DownloadIcon fontSize="small"/>
                            }
                            disabled={exportingZip}
                            onClick={() => void handleDownloadZip()}
                        >
                            Download Zip
                        </Button>
                        <IconButton aria-label="close" onClick={onClose}>
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </Box>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{minHeight: 36, borderTop: 1, borderColor: 'divider', pt: 0.5}}
                >
                    {TAB_DEFS.map((t) => (
                        <Tab
                            key={t.key}
                            value={t.key}
                            label={t.label}
                            sx={{minHeight: 36, py: 0.5, textTransform: 'none'}}
                        />
                    ))}
                </Tabs>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    overflow: tab === 'BS_PL' || tab === 'TAX_RETURN' ? 'hidden' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {visited.has('BS_PL') && (
                    <Box
                        sx={{
                            display: tab === 'BS_PL' ? 'flex' : 'none',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            height: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <BsPlPreviewPanel
                            companyId={companyId}
                            annualId={annualId}
                            active={tab === 'BS_PL'}
                            yearOfAssessment={yoa}
                        />
                    </Box>
                )}
                {visited.has('TAX_COM') && (
                    <Box sx={{display: tab === 'TAX_COM' ? 'block' : 'none'}}>
                        <TaxComPreviewPanel
                            companyId={companyId}
                            annualId={annualId}
                            active={tab === 'TAX_COM'}
                            periods={periods}
                            initialYearOfAssessment={initialYearOfAssessment}
                            onRequestCloseParent={onClose}
                        />
                    </Box>
                )}
                {visited.has('TAX_RETURN') && (
                    <Box
                        sx={{
                            display: tab === 'TAX_RETURN' ? 'flex' : 'none',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            height: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <TaxReturnPreviewPanel
                            companyId={companyId}
                            annualId={annualId}
                            yearOfAssessment={initialYearOfAssessment}
                            active={tab === 'TAX_RETURN'}
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
